import { useCallback } from 'react';

import { useUsersPageDeleteUserMutation } from '../../../generated';

export type CaptureExceptionFunction = (error: Error) => void;

export type DeleteUserInput = {
  userId: string;
};

export type UseDeleteUserResponse = {
  deleteUser: (input: DeleteUserInput) => Promise<boolean>;
  isLoading: boolean;
};

export type UseDeleteUserConfig = {
  captureException?: CaptureExceptionFunction;
};

export const useDeleteUser = (
  config: UseDeleteUserConfig
): UseDeleteUserResponse => {
  const { captureException } = config;

  const [deleteUserMutation, { loading }] = useUsersPageDeleteUserMutation();

  const deleteUser = useCallback(
    async (input: DeleteUserInput): Promise<boolean> => {
      try {
        const result = await deleteUserMutation({
          variables: {
            input: {
              userId: input.userId,
            },
          },
        });

        const deleteResult = result.data?.userRequestToDelete;

        if (deleteResult?.__typename === 'UserRequestToDeleteDetails') {
          return deleteResult.success;
        }

        throw new Error('Failed to delete user');
      } catch (error) {
        captureException?.(error as Error);
        throw error;
      }
    },
    [deleteUserMutation, captureException]
  );

  return {
    deleteUser,
    isLoading: loading,
  };
};
